/**
 * Shared Wound Assessment field contract for all Nurse Eval & Visit Notes forms.
 * Shape matches Eval & Visit Notes screenshots: surgical wound, body map,
 * and repeatable Wound #N assessments (care + NPWT).
 */

import {
  labelsFromWoundMapSites,
  woundLocationFromSites,
  woundMapSitesFromLocationText,
} from "./woundMapRegions";

export const MAX_WOUNDS = 8;

export const WOUND_YES_NO_OPTIONS = ["Yes", "No"];

export const INCISION_CLOSED_WITH_OPTIONS = [
  "Staples",
  "Sutures",
  "Steri-strips",
  "Skin/surgical adhesive",
  "Other",
];

export const WOUND_TYPE_OPTIONS = [
  "Pressure Ulcer",
  "Surgical",
  "Trauma",
  "Venous",
  "Arterial",
  "Diabetic / Neuropathic",
  "Skin Tear",
  "Other",
];

export const WOUND_STAGE_OPTIONS = [
  "1",
  "2",
  "3",
  "4",
  "Unstageable",
  "DTI",
  "N/A",
];

export const SURROUNDING_TISSUE_OPTIONS = [
  "Normal for ethnic group",
  "Pale",
  "Blanched",
  "Shiny",
  "Red",
  "Purple",
  "Black",
  "Edema",
  "Cool",
  "Warm",
  "Other",
];

export const WOUND_BED_TISSUE_OPTIONS = [
  "Bloody",
  "Granular",
  "Sloughing",
  "Weeping",
  "Necrotic",
  "Healthy",
  "Eschar",
  "Other",
];

export const DRAINAGE_TYPE_OPTIONS = [
  "None",
  "Serous",
  "Sanguineous",
  "Serosanguineous",
  "Purulent",
];

export const DRAINAGE_AMOUNT_OPTIONS = [
  "None",
  "Scant",
  "Minimal",
  "Moderate",
  "Copious",
];

export const NPWT_FOAM_OPTIONS = ["Black", "White", "Green"];

export const NPWT_PERIWOUND_OPTIONS = [
  "Skin prep",
  "Barrier film",
  "Hydrocolloid",
  "Foam",
  "Other",
];

export const NPWT_WOUND_BED_OPTIONS = [
  "Granulation",
  "Slough",
  "Eschar",
  "Mixed",
  "Other",
];

export const NPWT_PRESSURE_MODE_OPTIONS = ["Continuous", "Intermittent"];

export const toggleInWoundList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

let woundSeq = 0;
export const newWoundId = () => {
  woundSeq += 1;
  return `wound-${Date.now()}-${woundSeq}`;
};

export const emptyWoundEntry = (overrides = {}) => ({
  id: newWoundId(),
  location: "",
  type: "",
  stage: "",
  length: "",
  width: "",
  sqCm: "",
  depth: "",
  undermining: "",
  tunneling: "",
  surroundingTissue: [],
  surroundingTissueOther: "",
  woundBedColor: "",
  woundBedTissue: [],
  woundBedTissueOther: "",
  drainageType: "",
  drainageAmount: "",
  drainageOdor: "",
  careNotDueToday: false,
  cleansedWith: "",
  packedWith: "",
  dressedWith: "",
  coveredWith: "",
  securedWith: "",
  npwtType: "",
  npwtFoam: [],
  npwtPieces: "",
  npwtPeriwoundProtection: "",
  npwtWoundBed: "",
  npwtPressure: "",
  npwtPressureMode: "",
  npwtCanisterChange: false,
  patientResponse: "",
  ...overrides,
});

/** Canonical empty wound fields (spread into each nurse form emptyValues). */
export const emptyWoundAssessmentValues = () => ({
  surgicalWoundLocation: "",
  incisionClosedWith: [],
  incisionClosedOther: "",
  incisionSizeAppearance: "",

  woundPresent: "",
  woundLocation: "",
  woundMapSites: [],
  wounds: [],

  woundCareEducation: "",
  woundMapNotes: "",
  woundMapPatientId: "",
  woundMapDate: "",
  woundMapClinician: "",
});

/** Identified-wound fields only — surgical wound is left as entered. */
export const emptyIdentifiedWoundFields = () => ({
  woundPresent: "",
  woundLocation: "",
  woundMapSites: [],
  wounds: [],
  woundCareEducation: "",
  woundMapNotes: "",
});

/** Length × width in cm, blank when either side is not numeric. */
export function computeWoundAreaSqCm(length, width) {
  const l = Number.parseFloat(String(length || "").trim());
  const w = Number.parseFloat(String(width || "").trim());
  if (!Number.isFinite(l) || !Number.isFinite(w)) return "";
  const area = l * w;
  if (!Number.isFinite(area)) return "";
  return String(Math.round(area * 100) / 100);
}

/** Top-level keys that must remain arrays after load/merge. */
export const WOUND_ARRAY_KEYS = [
  "incisionClosedWith",
  "woundMapSites",
];

/**
 * Coerce Yes/No stored as string[] (legacy Discharge) or string into exclusive string.
 * @param {unknown} value
 * @returns {""|"Yes"|"No"}
 */
export function normalizeExclusiveYesNo(value) {
  if (Array.isArray(value)) {
    if (value.includes("Yes")) return "Yes";
    if (value.includes("No")) return "No";
    return "";
  }
  const s = String(value || "").trim();
  if (s === "Yes" || s === "No") return s;
  return "";
}

const asStringArray = (value) =>
  Array.isArray(value) ? value.filter(Boolean).map(String) : [];

const mapLegacyDrainageAmount = (value) => {
  if (Array.isArray(value)) {
    const first = value.find(Boolean);
    return mapLegacyDrainageAmount(first);
  }
  const s = String(value || "").trim();
  if (!s) return "";
  const legacyMap = {
    Scant: "Scant",
    Mod: "Moderate",
    Moderate: "Moderate",
    Large: "Copious",
    Heavy: "Copious",
    Minimal: "Minimal",
    None: "None",
    Copious: "Copious",
  };
  return legacyMap[s] || s;
};

const normalizeWoundEntry = (raw, index = 0) => {
  const src = raw && typeof raw === "object" ? raw : {};
  const base = emptyWoundEntry({ id: String(src.id || `wound-${index + 1}`) });
  return {
    ...base,
    location: String(src.location || src.woundLocation || "").trim(),
    type: String(src.type || src.woundType || src.woundTypeStage || "").trim(),
    stage: String(src.stage || "").trim(),
    length: String(src.length || src.woundLength || "").trim(),
    width: String(src.width || src.woundWidth || "").trim(),
    sqCm: String(src.sqCm || "").trim(),
    depth: String(src.depth || src.woundDepth || "").trim(),
    undermining: String(src.undermining || "").trim(),
    tunneling: String(src.tunneling || "").trim(),
    surroundingTissue: asStringArray(src.surroundingTissue),
    surroundingTissueOther: String(src.surroundingTissueOther || "").trim(),
    woundBedColor: String(src.woundBedColor || "").trim(),
    woundBedTissue: asStringArray(src.woundBedTissue),
    woundBedTissueOther: String(src.woundBedTissueOther || "").trim(),
    drainageType: String(src.drainageType || "").trim(),
    drainageAmount: mapLegacyDrainageAmount(
      src.drainageAmount || src.woundDrainage,
    ),
    drainageOdor: String(src.drainageOdor || "").trim(),
    careNotDueToday: Boolean(src.careNotDueToday),
    cleansedWith: String(src.cleansedWith || "").trim(),
    packedWith: String(src.packedWith || "").trim(),
    dressedWith: String(src.dressedWith || "").trim(),
    coveredWith: String(src.coveredWith || "").trim(),
    securedWith: String(src.securedWith || "").trim(),
    npwtType: String(src.npwtType || "").trim(),
    npwtFoam: asStringArray(src.npwtFoam),
    npwtPieces: String(src.npwtPieces || "").trim(),
    npwtPeriwoundProtection: String(src.npwtPeriwoundProtection || "").trim(),
    npwtWoundBed: String(src.npwtWoundBed || "").trim(),
    npwtPressure: String(src.npwtPressure || "").trim(),
    npwtPressureMode: String(src.npwtPressureMode || "").trim(),
    npwtCanisterChange: Boolean(src.npwtCanisterChange),
    patientResponse: String(src.patientResponse || "").trim(),
  };
};

const migrateLegacySingleWound = (src) => {
  const hasLegacy =
    Boolean(String(src.woundLocation || "").trim()) ||
    Boolean(String(src.woundType || src.woundTypeStage || "").trim()) ||
    Boolean(String(src.woundLength || src.woundSize || "").trim()) ||
    Boolean(String(src.woundCareProvided || "").trim());
  if (!hasLegacy) return [];

  let length = String(src.woundLength || "").trim();
  const width = String(src.woundWidth || "").trim();
  const depth = String(src.woundDepth || "").trim();
  const woundSize = String(src.woundSize || "").trim();
  if (woundSize && !length && !width && !depth) length = woundSize;

  const care = String(src.woundCareProvided || "").trim();
  return [
    normalizeWoundEntry({
      id: "wound-1",
      location: src.woundLocation,
      type: src.woundType || src.woundTypeStage,
      length,
      width,
      depth,
      drainageAmount: src.woundDrainage,
      cleansedWith: care,
    }),
  ];
};

/**
 * Merge / migrate wound fields from any nurse form payload into the shared shape.
 * @param {Record<string, unknown>} raw
 * @param {{ mrn?: string, clinicianName?: string }} [prefs]
 */
export function normalizeWoundAssessmentPayload(raw = {}, prefs = {}) {
  const base = emptyWoundAssessmentValues();
  const src = raw && typeof raw === "object" ? raw : {};

  const woundLocation = String(src.woundLocation || "").trim();
  let woundMapSites = Array.isArray(src.woundMapSites)
    ? src.woundMapSites.map(String).filter(Boolean)
    : [];
  if (!woundMapSites.length && woundLocation) {
    woundMapSites = woundMapSitesFromLocationText(woundLocation);
  }

  let wounds = Array.isArray(src.wounds)
    ? src.wounds.map((row, index) => normalizeWoundEntry(row, index))
    : [];
  if (!wounds.length) {
    wounds = migrateLegacySingleWound(src);
  }
  const woundPresent = normalizeExclusiveYesNo(src.woundPresent);
  if (!wounds.length && woundPresent === "Yes") {
    const labels = labelsFromWoundMapSites(woundMapSites);
    if (labels.length) {
      wounds = labels.map((location, index) =>
        emptyWoundEntry({ id: `wound-${index + 1}`, location }),
      );
    } else {
      wounds = [
        emptyWoundEntry({
          id: "wound-1",
          location: woundLocation,
        }),
      ];
    }
  }
  if (wounds.length > MAX_WOUNDS) wounds = wounds.slice(0, MAX_WOUNDS);

  const mapNotesParts = [
    String(src.woundMapNotes || "").trim(),
  ].filter(Boolean);

  return {
    ...base,
    surgicalWoundLocation: String(src.surgicalWoundLocation || "").trim(),
    incisionClosedWith: asStringArray(src.incisionClosedWith),
    incisionClosedOther: String(src.incisionClosedOther || "").trim(),
    incisionSizeAppearance: String(src.incisionSizeAppearance || "").trim(),
    woundPresent,
    woundLocation:
      woundLocationFromSites(woundMapSites) ||
      String(src.woundLocation || "").trim() ||
      (wounds[0]?.location || ""),
    woundMapSites,
    wounds,
    woundCareEducation: normalizeExclusiveYesNo(src.woundCareEducation),
    woundMapNotes: mapNotesParts.join("\n"),
    woundMapPatientId:
      String(src.woundMapPatientId || "").trim() ||
      String(prefs.mrn || "").trim() ||
      "",
    woundMapDate: String(src.woundMapDate || "").trim(),
    woundMapClinician:
      String(src.woundMapClinician || "").trim() ||
      String(prefs.clinicianName || "").trim() ||
      "",
  };
}

export { woundLocationFromSites };

export const LEGACY_WOUND_KEYS = [
  "woundType",
  "woundTypeStage",
  "woundCurrentStatus",
  "woundLength",
  "woundWidth",
  "woundDepth",
  "woundSize",
  "woundDrainage",
  "woundCareProvided",
];

/**
 * Normalize wound fields onto a form values object and drop legacy single-wound keys.
 * @param {Record<string, unknown>} target
 * @param {Record<string, unknown>} raw
 * @param {{ mrn?: string, clinicianName?: string }} [prefs]
 */
export function applyWoundAssessmentPayload(target, raw = target, prefs = {}) {
  const next = normalizeWoundAssessmentPayload(raw, prefs);
  LEGACY_WOUND_KEYS.forEach((key) => {
    delete target[key];
  });
  Object.assign(target, next);
  return target;
}

/**
 * Pick only wound keys from form values for save payloads (optional helper).
 * @param {Record<string, unknown>} values
 */
export function pickWoundAssessmentValues(values = {}) {
  const keys = Object.keys(emptyWoundAssessmentValues());
  const out = {};
  keys.forEach((k) => {
    out[k] = values[k];
  });
  return out;
}
